import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import * as z from "zod/v4";
import { useMemo, useState } from "react";
import { FilterIcon } from "lucide-react";
import {
  DateRangeFilter,
  EntityFilters,
  MatrixTable,
  SummaryTable,
} from "../-components";
import type { MatrixRow, SummaryRow } from "../-components";
import {
  useDepartmentUserDetections,
  useEntityCategories,
} from "@/lib/queries/desktop-statistics";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";

function formatDate(d: Date) {
  return d.toISOString().slice(0, 10);
}
const DEFAULT_END_DATE = formatDate(new Date());
const DEFAULT_START_DATE = formatDate(
  new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
);

const searchSchema = z.object({
  start_date: z.string().default(DEFAULT_START_DATE).catch(DEFAULT_START_DATE),
  end_date: z.string().default(DEFAULT_END_DATE).catch(DEFAULT_END_DATE),
  entity_category: z.string().optional().catch(undefined),
  entity_id: z.coerce.number().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/edge-device-statistics/$depId/")({
  params: {
    parse: z.object({ depId: z.coerce.number().int() }).parse,
  },
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  staticData: {
    permissionGuards: [
      {
        resource: "statistics",
      },
    ],
  },
});

function RouteComponent() {
  const { depId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const categoriesQuery = useEntityCategories();
  const detectionsQuery = useDepartmentUserDetections({
    department_id: Number(depId),
    start_date: search.start_date,
    end_date: search.end_date,
  });

  const isLoading = categoriesQuery.isLoading || detectionsQuery.isLoading;
  const isError = categoriesQuery.isError || detectionsQuery.isError;

  const categories = categoriesQuery.data?.categories ?? [];
  const deptData = detectionsQuery.data;
  const users = deptData?.users ?? [];

  const visibleCategories = useMemo(() => {
    if (!search.entity_category) return categories;
    const filtered = categories
      .filter((c) => c.name === search.entity_category)
      .map((c) => ({
        ...c,
        entities: search.entity_id
          ? c.entities.filter((e) => e.entity_id === search.entity_id)
          : c.entities,
      }));
    return filtered;
  }, [categories, search.entity_category, search.entity_id]);

  const buildUserHref = (userId: string, userName: string) =>
    `/edge-device-statistics/${depId}/${userId}?user_name=${encodeURIComponent(userName)}`;

  const matrixRows = useMemo<Array<MatrixRow>>(
    () =>
      users.map((u) => ({
        id: u.user_id,
        name: u.user_name,
        total: u.total,
        entities: u.entities,
        href: buildUserHref(u.user_id, u.user_name),
      })),
    [users, depId],
  );

  const summaryRows = useMemo<Array<SummaryRow>>(
    () =>
      users.map((u) => ({
        id: u.user_id,
        name: u.user_name,
        total: u.total,
        critical: u.critical,
        href: buildUserHref(u.user_id, u.user_name),
      })),
    [users, depId],
  );

  const activeFilterCount = [search.entity_category, search.entity_id].filter(
    Boolean,
  ).length;

  function updateSearch(updates: Partial<typeof search>) {
    navigate({ to: ".", search: { ...search, ...updates } });
  }

  return (
    <div className="flex w-full flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            <a
              href="/edge-device-statistics"
              className="hover:text-foreground hover:underline"
            >
              Edge Device Statistics
            </a>
            {" / "}
            <span>{deptData?.department_name ?? ""}</span>
          </p>
          <h1 className="text-3xl font-bold">
            {deptData?.department_name ?? "Department"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeFilter
            startDate={search.start_date}
            endDate={search.end_date}
            onApply={(start_date, end_date) =>
              updateSearch({ start_date, end_date })
            }
          />
          <EntityFilterPopover
            search={search}
            categories={categories}
            onApply={(filters) => updateSearch(filters)}
            activeCount={activeFilterCount}
          />
        </div>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      )}

      {isError && (
        <div className="flex h-64 flex-col items-center justify-center gap-2 text-muted-foreground">
          <p>Error loading data.</p>
          {detectionsQuery.error && (
            <pre className="max-w-xl text-xs text-destructive">
              {String(detectionsQuery.error)}
            </pre>
          )}
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <MatrixTable
              categories={visibleCategories}
              rows={matrixRows}
              rowLabel="User"
            />
          </div>
          <SummaryTable rows={summaryRows} nameLabel="User" />
        </>
      )}
    </div>
  );
}

// --- Entity-only Filter Popover ---

type EntityFilterValues = {
  entity_category?: string;
  entity_id?: number;
};

function EntityFilterPopover({
  search,
  categories,
  onApply,
  activeCount,
}: {
  search: EntityFilterValues;
  categories: Parameters<typeof EntityFilters>[0]["categories"];
  onApply: (filters: EntityFilterValues) => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [entityCategory, setEntityCategory] = useState(
    search.entity_category ?? "",
  );
  const [entityId, setEntityId] = useState(search.entity_id?.toString() ?? "");

  function handleOpenChange(val: boolean) {
    if (val) {
      setEntityCategory(search.entity_category ?? "");
      setEntityId(search.entity_id?.toString() ?? "");
    }
    setOpen(val);
  }

  function handleApply() {
    onApply({
      entity_category: entityCategory || undefined,
      entity_id: entityId ? Number(entityId) : undefined,
    });
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="relative gap-2">
          <FilterIcon className="h-4 w-4" />
          Filters
          {activeCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Filters</p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={() => {
                setEntityCategory("");
                setEntityId("");
              }}
            >
              Reset all
            </Button>
          </div>

          <EntityFilters
            categories={categories}
            entityCategory={entityCategory}
            entityId={entityId}
            onCategoryChange={setEntityCategory}
            onEntityChange={setEntityId}
          />

          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button className="flex-1" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
